import { z } from 'zod';
import { CARD_TITLE_MAX_LENGTH, SHORT_LINK_MAX_LENGTH } from './board';
import { cardTitleSchema } from './card';
import { cardCoverSchema } from './card-cover';
import { dueReminderOffsetSchema } from './card-dates';
import { checklistTitleSchema } from './checklist';
import { ulidSchema } from './id';

/**
 * The personal Inbox — B43's backend contract for `FR-NOTIF-015` and
 * `FR-NOTIF-018`.
 *
 * Inbox cards are explicitly boardless: `boardId`, `listId`, `position` and
 * `cardNumber` are `null` here rather than optional. `cardSchema` stays
 * unchanged and continues to describe ordinary board cards only.
 */

const inboxCardNeighbourSchema = ulidSchema.nullable().default(null);
const inboxTimestampSchema = z.string().min(1).max(64);
const inboxQueryTimestampSchema = z.string().trim().min(1).max(64);

/** `user_preferences.inbox_background_value varchar(255)`. */
export const INBOX_BACKGROUND_VALUE_MAX_LENGTH = 255;

/** `attachments.name varchar(255)` for capture descriptors. */
export const INBOX_CAPTURE_ATTACHMENT_NAME_MAX_LENGTH = 255;

/** MIME type label stored beside provider/app attachment metadata. */
export const INBOX_CAPTURE_ATTACHMENT_CONTENT_TYPE_MAX_LENGTH = 255;

/** Shared URL/reference width used by capture descriptors. */
export const INBOX_CAPTURE_LINK_MAX_LENGTH = 2048;

/** `user_preferences.inbox_background_type` — nullable column vocabulary. */
export const inboxBackgroundTypeSchema = z.enum(['colour', 'photo']);

export type InboxBackgroundType = z.infer<typeof inboxBackgroundTypeSchema>;

export const inboxCaptureSourceSchema = z.enum([
  'mobile_share',
  'voice',
  'connected_app',
  'chat',
]);

export type InboxCaptureSource = z.infer<typeof inboxCaptureSourceSchema>;

export const inboxCaptureAttachmentSchema = z.strictObject({
  name: z.string().trim().min(1).max(INBOX_CAPTURE_ATTACHMENT_NAME_MAX_LENGTH),
  contentType: z
    .string()
    .trim()
    .min(1)
    .max(INBOX_CAPTURE_ATTACHMENT_CONTENT_TYPE_MAX_LENGTH)
    .optional(),
  size: z.number().int().nonnegative(),
  url: z.url().max(INBOX_CAPTURE_LINK_MAX_LENGTH).optional(),
  sourceLink: z.url().max(INBOX_CAPTURE_LINK_MAX_LENGTH).optional(),
});

export type InboxCaptureAttachment = z.infer<typeof inboxCaptureAttachmentSchema>;

/**
 * `POST /me/inbox/cards` — capture one card in the caller's private Inbox.
 *
 * B43's direct capture path accepts a title only. Descriptions, dates,
 * checklists, covers and attachments are existing card capabilities that can
 * be edited after creation; board-dependent capabilities are absent until the
 * card is filed.
 */
export const createInboxCardBodySchema = z.strictObject({
  title: cardTitleSchema.describe(
    'The captured card title. Trimmed; a whitespace-only title is refused.',
  ),
});

export type CreateInboxCardBody = z.infer<typeof createInboxCardBodySchema>;

/**
 * `POST /me/inbox/cards/:id/file` — move an Inbox card to a board list.
 *
 * The destination board is derived from `listId`, exactly as B12 card creation
 * derives a board from its list. The caller names neighbours, never a rank.
 */
export const fileInboxCardBodySchema = z.strictObject({
  listId: ulidSchema.describe(
    'The destination list. The destination board is read from this list and is ' +
      'not accepted from the caller.',
  ),
  previousCardId: inboxCardNeighbourSchema.describe(
    'The card immediately above the destination gap. Omit both neighbours to ' +
      'file at the bottom of the destination list.',
  ),
  nextCardId: inboxCardNeighbourSchema.describe(
    'The card immediately below the destination gap. Send this alone to file ' +
      'at the top of the destination list.',
  ),
});

export type FileInboxCardBody = z.infer<typeof fileInboxCardBodySchema>;

/**
 * One card in `GET /me/inbox/cards`, and the response body of creating one.
 *
 * This is not `cardSchema` widened in place. It carries the same card identity
 * columns where they exist, makes the board-card parent columns null by
 * construction, and adds only the already-built card attributes useful on a
 * personal capture list.
 */
export const inboxCardSchema = z.object({
  id: ulidSchema,
  inboxOwnerId: ulidSchema,
  boardId: z.null(),
  listId: z.null(),
  title: z.string().max(CARD_TITLE_MAX_LENGTH),
  description: z.string().nullable(),
  shortLink: z.string().max(SHORT_LINK_MAX_LENGTH),
  position: z.null(),
  cardNumber: z.null(),
  /** No board, no number, so no reference — the path keeps the short link. */
  ref: z.null(),
  startAt: z.string().min(1).nullable(),
  dueAt: z.string().min(1).nullable(),
  dueReminderMinutesBefore: dueReminderOffsetSchema.nullable(),
  completed: z.boolean(),
  cover: cardCoverSchema.nullable(),
  checklistItemCount: z.number().int().nonnegative(),
  checklistCheckedItemCount: z.number().int().nonnegative(),
  attachmentCount: z.number().int().nonnegative(),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
});

export type InboxCard = z.infer<typeof inboxCardSchema>;

export const inboxCardListSchema = z.array(inboxCardSchema);

export type InboxCardList = z.infer<typeof inboxCardListSchema>;

export const inboxCardMutationResultSchema = z.strictObject({
  card: inboxCardSchema,
});

export type InboxCardMutationResult = z.infer<typeof inboxCardMutationResultSchema>;

/**
 * `GET /me/inbox/email-address` and
 * `POST /me/inbox/email-address/regenerate`.
 */
export const inboxEmailAddressViewSchema = z.strictObject({
  userId: ulidSchema,
  address: z.string().email().max(320),
  active: z.boolean(),
  regenerable: z.boolean(),
  updatedAt: inboxTimestampSchema,
});

export type InboxEmailAddressView = z.infer<typeof inboxEmailAddressViewSchema>;

export const inboxEmailAddressResultSchema = z.strictObject({
  inboxEmailAddress: inboxEmailAddressViewSchema,
});

export type InboxEmailAddressResult = z.infer<typeof inboxEmailAddressResultSchema>;

export const inboundInboxEmailResultSchema = z.strictObject({
  status: z.enum(['accepted', 'rejected', 'ignored', 'duplicate']),
  cardId: ulidSchema.nullable(),
  reason: z
    .enum([
      'created',
      'duplicate_message',
      'unknown_recipient',
      'unverified_sender',
      'oversize',
      'empty_message',
      'invalid_payload',
    ])
    .nullable(),
});

export type InboundInboxEmailResult = z.infer<typeof inboundInboxEmailResultSchema>;

/**
 * `POST /me/inbox/captures` and connected-app Inbox capture.
 *
 * The bearer-token route authorizes the caller differently, but the captured
 * work item is the same boardless Inbox card request.
 */
export const authenticatedInboxCaptureBodySchema = z.strictObject({
  source: inboxCaptureSourceSchema,
  title: cardTitleSchema,
  description: z.string().trim().min(1).max(16_384).optional(),
  url: z.url().max(INBOX_CAPTURE_LINK_MAX_LENGTH).optional(),
  sourceLink: z.url().max(INBOX_CAPTURE_LINK_MAX_LENGTH).optional(),
  attachments: z.array(inboxCaptureAttachmentSchema).max(20).default([]),
});

export type AuthenticatedInboxCaptureBody = z.infer<
  typeof authenticatedInboxCaptureBodySchema
>;

export const connectedInboxCaptureBodySchema = authenticatedInboxCaptureBodySchema.extend(
  {
    source: z.literal('connected_app'),
  },
);

export type ConnectedInboxCaptureBody = z.infer<typeof connectedInboxCaptureBodySchema>;

export const inboxCardsSortSchema = z.enum(['newest', 'oldest', 'alphabetical']);

export type InboxCardsSort = z.infer<typeof inboxCardsSortSchema>;

export const inboxCompletionFilterSchema = z.enum(['any', 'complete', 'incomplete']);

export type InboxCompletionFilter = z.infer<typeof inboxCompletionFilterSchema>;

export const inboxCardsQuerySchema = z.strictObject({
  keyword: z.string().trim().min(1).max(512).optional(),
  createdFrom: inboxQueryTimestampSchema.optional(),
  createdTo: inboxQueryTimestampSchema.optional(),
  completed: inboxCompletionFilterSchema.default('any'),
  dueFrom: inboxQueryTimestampSchema.optional(),
  dueTo: inboxQueryTimestampSchema.optional(),
  archived: z.stringbool({ truthy: ['true'], falsy: ['false'] }).default(false),
  sort: inboxCardsSortSchema.default('newest'),
});

export type InboxCardsQuery = z.infer<typeof inboxCardsQuerySchema>;

export const inboxCardArchiveResponseSchema = z.strictObject({
  card: inboxCardSchema,
  archived: z.literal(true),
});

export type InboxCardArchiveResponse = z.infer<typeof inboxCardArchiveResponseSchema>;

export const inboxCardRestoreResponseSchema = z.strictObject({
  card: inboxCardSchema,
  archived: z.literal(false),
});

export type InboxCardRestoreResponse = z.infer<typeof inboxCardRestoreResponseSchema>;

/**
 * `PATCH /me/inbox/background`.
 *
 * Null clears the independent Inbox background. Strict variants make unrelated
 * user_preferences columns impossible to send through this field-scoped route.
 */
export const inboxBackgroundPatchSchema = z.discriminatedUnion('type', [
  z.strictObject({
    type: z.literal('colour'),
    value: z.string().trim().min(1).max(INBOX_BACKGROUND_VALUE_MAX_LENGTH),
  }),
  z.strictObject({
    type: z.literal('photo'),
    value: z.string().trim().min(1).max(INBOX_BACKGROUND_VALUE_MAX_LENGTH),
  }),
  z.strictObject({
    type: z.literal('none'),
    value: z.null().default(null),
  }),
]);

export type InboxBackgroundPatch = z.infer<typeof inboxBackgroundPatchSchema>;

/**
 * `POST /me/inbox/cards/:id/checklists` — BE-43.1's fix for the field-scoped
 * half of `FR-NOTIF-015` AC3 ("a due date and checklist can be set" on an
 * unfiled Inbox card).
 *
 * ⚠ **Title only — no `sourceChecklistId`.** `checklistCreateBodySchema`
 * (`./checklist`) is board-card shaped: its copy field reads "any card the
 * caller can read", and resolving that scope across the Inbox/board boundary
 * (may a caller copy a board checklist onto their own Inbox card, and does
 * filing later re-check it?) is exactly the kind of open design question
 * BE-64.2's mirror-write finding warns against answering implicitly inside a
 * schema. An empty checklist is `FR-CARD-016` AC1's own "no source" case, so
 * this is not a narrower feature — it is the same one, minus a decision that
 * was never made for the Inbox.
 */
export const inboxChecklistCreateBodySchema = z.strictObject({
  title: checklistTitleSchema.describe(
    'The checklist title. Trimmed; an empty or whitespace-only submission ' +
      'is refused (422). Required — there is no server-side default.',
  ),
});

export type InboxChecklistCreateBody = z.infer<typeof inboxChecklistCreateBodySchema>;
